import {getJSON, postJSON, putJSON, safeDelete} from 'lib/utils/safe-fetch'

/**
 * A typed set of utilities for fetching collections and records from the database over HTTP.
 */
export default class ClientCollection<T> {
  name: string
  baseUrl: string

  constructor(name: string) {
    this.baseUrl = `/api/db/${name}`
    this.name = name
  }

  /**
   * Create a URL with this ID.
   */
  getUrl(_id: string): string {
    return `${this.baseUrl}/${_id}`
  }

  /**
   * Fetch a record via this ID.
   */
  async get(_id: string): Promise<T> {
    const url = this.getUrl(_id)
    const res = await getJSON<T>(url)
    if (res.ok === false) throw res.error
    return res.data
  }

  /**
   * Create a new record by posting to the API.
   * @param properties
   * @returns new record
   */
  async create(properties: Partial<T>): Promise<T> {
    const res = await postJSON<typeof properties, T>(this.baseUrl, properties)
    if (res.ok === false) throw res.error
    return res.data
  }

  /**
   * Remove via API
   */
  async remove(_id: string): Promise<void> {
    const url = this.getUrl(_id)
    const res = await safeDelete(url)
    if (res.ok === false) throw res.error
  }

  /**
   * Upate a single record.
   * @param _id
   * @param newProperties
   * @returns updated record
   */
  async updateOne(_id: string, newProperties: Partial<T>): Promise<T> {
    const url = this.getUrl(_id)
    const res = await putJSON<T>(url, newProperties)
    if (res.ok === false) throw res.error
    return res.data
  }

  /**
   * Update a list of records.
   * @param newValues
   * @returns updated records
   */
  async updateMany(newValues: T[]) {
    // Update the values via the API first
    const res = await putJSON(this.baseUrl, newValues)
    if (res.ok === false) throw res.error
    return res.data
  }
}
